import { maybe } from '@passionware/monads';
import { ensureError } from '@passionware/platform-js';
import { QueryClient, useQuery } from '@tanstack/react-query';
import get from 'lodash/get';
import store from '../../../../store';
import { formatBooking } from 'store/v1/bookings/bookings.formatter.js';
import { getBookingExpenses } from 'store/v1/expenses/expenses.actions.js';
import { PRODUCTIONS_ACTIONS } from '../../../../store/v1/productions/productions.constants.js';
import { createBookingObject } from 'v1/helpers/byModel/BookingHelper';
import type { Booking } from '__types__';
import { BookingApi, BookingCreateDTO } from 'v5/api/booking/booking.api';
import { type ProductionApi } from 'v5/api/production/production.api';
import type { ResourcingApi } from 'v5/api/resourcing/resourcing.api';
import {
  type BudgetExpenseLineCreateInput,
  type CreateResourceAssignmentResourceInput
} from 'v5/core/production-resourcing/mutation-inputs';
import {
  bookingBillingFromV1,
  type BookingEventFields
} from 'v5/api/booking/bookingBillingFromV1.http';
import { pickInitialBookingStatus } from 'v5/api/booking/pickInitialBookingStatusFromV1.http';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import { WithServices } from 'v5/platform/ts/services';
import { WithErrorService } from 'v5/services/front/ErrorService/ErrorService';
import { WithMessageService } from 'v5/services/internal/MessageService/MessageService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { getDefaultExpenseCategoryId } from './_private/productionMutationResourcingDefaults';
import {
  awaitReportSystemEffects,
  getSystemEffectsForExpenseUpdate,
  getSystemEffectsForProductionResourcingUpdate
} from 'v5/services/internal/systemEffects/productionResourcingSystemEffects';
import type {
  CreatePersistOptions,
  PersistOptions
} from 'v5/api/_common/mutation/persistOptions';
import { ProductionMutationService } from './ProductionMutationService';

function associationsQueryKey(productionId: number) {
  return ['production', 'associations', productionId] as const;
}

function mergeResultForV1Reducer(
  bundle: Awaited<ReturnType<ProductionApi['getProductionAssociations']>>
) {
  return {
    ...bundle.raw,
    notes: bundle.notes,
    documents: bundle.documents
  };
}

export function createProductionMutationService(
  config: WithServices<
    [WithErrorService, WithMessageService, WithResourceService]
  > & {
    api: ProductionApi;
    bookingApi: BookingApi;
    resourcingApi: ResourcingApi;
    queryClient: QueryClient;
  }
): ProductionMutationService {
  const {
    api,
    bookingApi,
    resourcingApi,
    queryClient,
    services: { errorService, messageService, resourceService }
  } = config;
  const interceptor = errorService.withCategory('ProductionMutationService');

  async function afterResourcingMutation(
    productionId: number,
    options?: { bookingIds?: readonly number[] }
  ) {
    await awaitReportSystemEffects(
      messageService,
      getSystemEffectsForProductionResourcingUpdate({
        productionId,
        bookingIds: options?.bookingIds
      })
    );
  }

  async function persistThenRefreshProduction<T extends { id: number }>(
    productionId: number,
    persist: () => Promise<T>,
    options?: CreatePersistOptions
  ): Promise<T> {
    const created = await persist();
    options?.onPersisted?.(created);
    await afterResourcingMutation(productionId);
    return created;
  }

  async function afterResourcingExpenseMutation(
    productionId: number,
    expenseLineId: number
  ) {
    await awaitReportSystemEffects(messageService, [
      ...getSystemEffectsForExpenseUpdate(expenseLineId),
      ...getSystemEffectsForProductionResourcingUpdate({ productionId })
    ]);
  }

  async function reportProductionAssociationsChanged(productionId: number) {
    await messageService.reportSystemEffect.sendRequest(
      {
        scope: [
          {
            entity: 'production',
            entityId: productionId,
            payload: { type: 'update' }
          }
        ]
      },
      3000
    );
  }

  const getProductionAssociations = interceptor.wrap(
    async (productionId: number) => {
      const bundle = await api.getProductionAssociations(productionId);
      store.dispatch({
        type: PRODUCTIONS_ACTIONS.GET_PRODUCTION_ASSOCIATIONS_SUCCESS,
        result: mergeResultForV1Reducer(bundle),
        production_id: productionId
      });
      return bundle;
    }
  );

  return {
    useAssociations: (productionId: number) =>
      ensureIdleQuery(
        productionId > 0 ? maybe.of(productionId) : maybe.ofAbsent(),
        useQuery(
          {
            queryKey: associationsQueryKey(productionId),
            queryFn: () => getProductionAssociations(productionId),
            enabled: Number.isFinite(productionId) && productionId > 0
          },
          queryClient
        )
      ),

    getProductionAssociations,

    createProductionNote: interceptor.wrap(
      async (productionId: number, payload: { note: string }) => {
        const note = await api.createProductionNote(productionId, payload);
        store.dispatch({
          type: PRODUCTIONS_ACTIONS.CREATE_PRODUCTION_NOTE_SUCCESS,
          result: note,
          production_id: productionId
        });
        await reportProductionAssociationsChanged(productionId);
      }
    ),

    deleteProductionNote: interceptor.wrap(
      async (productionId: number, noteId: number) => {
        await api.deleteProductionNote(productionId, noteId);
        store.dispatch({
          type: PRODUCTIONS_ACTIONS.DELETE_PRODUCTION_NOTE_SUCCESS,
          production_id: productionId,
          association_id: noteId
        });
        await reportProductionAssociationsChanged(productionId);
      }
    ),

    createProductionDocument: interceptor.wrap(
      async (
        productionId: number,
        payload: { file_id: string; url: string }
      ) => {
        const document = await api.createProductionDocument(
          productionId,
          payload
        );
        store.dispatch({
          type: PRODUCTIONS_ACTIONS.CREATE_PRODUCTION_DOCUMENT_SUCCESS,
          result: document,
          production_id: productionId
        });
        await reportProductionAssociationsChanged(productionId);
      }
    ),

    deleteProductionDocument: interceptor.wrap(
      async (productionId: number, documentId: number) => {
        await api.deleteProductionDocument(productionId, documentId);
        store.dispatch({
          type: PRODUCTIONS_ACTIONS.DELETE_PRODUCTION_DOCUMENT_SUCCESS,
          production_id: productionId,
          association_id: documentId
        });
        await reportProductionAssociationsChanged(productionId);
      }
    ),

    reorderProductionDocuments: interceptor.wrap(
      async (productionId: number, orderedIds: number[]) => {
        await api.reorderDocuments(orderedIds);
        await reportProductionAssociationsChanged(productionId);
      }
    ),

    updateResourceSlot: interceptor.wrap(
      async (productionId, slotId, patch) => {
        await resourcingApi.updateResourceSlot(productionId, slotId, patch);
        await afterResourcingMutation(productionId);
      }
    ),

    createResourceSlot: interceptor.wrap(
      async (productionId, input, options?: CreatePersistOptions) =>
        persistThenRefreshProduction(
          productionId,
          () => resourcingApi.createResourceSlot(productionId, input),
          options
        )
    ),

    deleteResourceSlot: interceptor.wrap(async (productionId, slotId) => {
      await resourcingApi.deleteResourceSlot(productionId, slotId);
      await afterResourcingMutation(productionId);
    }),

    reorderResourceSlots: interceptor.wrap(async (productionId, slots) => {
      await resourcingApi.reorderResourceSlots(slots);
      await afterResourcingMutation(productionId);
    }),

    addBudgetExpense: interceptor.wrap(
      async (productionId, input, options?: CreatePersistOptions) =>
        persistThenRefreshProduction(
          productionId,
          () => resourcingApi.addBudgetExpense(productionId, input),
          options
        )
    ),

    updateBudgetExpense: interceptor.wrap(
      async (productionId, expenseId, patch) => {
        await resourcingApi.updateBudgetExpense(productionId, expenseId, patch);
        await afterResourcingMutation(productionId);
      }
    ),

    deleteBudgetExpense: interceptor.wrap(async (productionId, expenseId) => {
      await resourcingApi.deleteBudgetExpense(productionId, expenseId);
      await afterResourcingMutation(productionId);
    }),

    orderBudgetExpenses: interceptor.wrap(async (productionId, expenses) => {
      await resourcingApi.orderBudgetExpenses(productionId, expenses);
      await afterResourcingMutation(productionId);
    }),

    createBudgetExpenseLine: interceptor.wrap(
      async (productionId, input, options?: CreatePersistOptions) => {
        const resolvedCategoryId =
          input.slotCategoryId ?? getDefaultExpenseCategoryId();
        if (resolvedCategoryId == null) {
          throw new Error('Missing expense category for expense line creation');
        }
        const payload: BudgetExpenseLineCreateInput = {
          productionBudgetExpenseId: input.productionBudgetExpenseId,
          slotCategoryId: resolvedCategoryId,
          vendor: input.vendor,
          charge: input.charge
        };
        return persistThenRefreshProduction(
          productionId,
          () => resourcingApi.createExpense(payload),
          options
        );
      }
    ),

    deleteExpenseLine: interceptor.wrap(async (productionId, expenseLineId) => {
      await resourcingApi.deleteExpense(expenseLineId);
      await afterResourcingExpenseMutation(productionId, expenseLineId);
    }),

    deleteResourceSlotAssignment: interceptor.wrap(
      async (productionId, slotId, assignmentId) => {
        await resourcingApi.deleteResourceSlotAssignment(
          productionId,
          slotId,
          assignmentId
        );
        await afterResourcingMutation(productionId);
      }
    ),

    createProductionGroup: interceptor.wrap(
      async (input, options?: CreatePersistOptions) =>
        persistThenRefreshProduction(
          input.entity_id,
          () => resourcingApi.createProductionGroup(input),
          options
        )
    ),

    updateProductionGroup: interceptor.wrap(
      async (productionId, groupId, input) => {
        await resourcingApi.updateProductionGroup(groupId, input);
        await afterResourcingMutation(productionId);
      }
    ),

    deleteProductionGroup: interceptor.wrap(async (productionId, groupId) => {
      await resourcingApi.deleteProductionGroup(productionId, groupId);
      await afterResourcingMutation(productionId);
    }),

    reorderProductionGroups: interceptor.wrap(async (productionId, groups) => {
      await resourcingApi.reorderProductionGroups(groups);
      await afterResourcingMutation(productionId);
    }),

    updateBooking: interceptor.wrap(async (productionId, bookingId, patch) => {
      const result = await bookingApi.updateResourcingBooking(bookingId, patch);
      await afterResourcingMutation(productionId, { bookingIds: [bookingId] });
      return result;
    }),

    updateBookingExpenses: interceptor.wrap(
      async (productionId, bookingId, patch) => {
        const result = await bookingApi.updateResourcingBookingExpenses(
          bookingId,
          patch
        );
        // Resourcing server state reads booking expenses from Redux, not RQ booking detail.
        const expenseFetch = await store.dispatch(
          getBookingExpenses([bookingId])
        );
        if (expenseFetch.error) {
          throw ensureError(expenseFetch.error);
        }
        await afterResourcingMutation(productionId, {
          bookingIds: [bookingId]
        });
        return result;
      }
    ),

    updateExpenseLine: interceptor.wrap(
      async (productionId, expenseLineId, patch) => {
        await bookingApi.updateResourcingExpenseLine(expenseLineId, patch);
        await afterResourcingExpenseMutation(productionId, expenseLineId);
      }
    ),

    createResourceAssignments: interceptor.wrap(
      async (productionId, input, options?: PersistOptions<Booking[]>) => {
        const state = store.getState() as Record<string, unknown>;
        const productionDate = get(state, [
          'productions',
          'data',
          productionId,
          'production_date'
        ]) as { start_date?: string; end_date?: string } | null;
        const bookingTypes = get(state, 'booking_types', {});
        const statuses = Object.values(
          (get(state, 'statuses.data', {}) as Record<
            string,
            {
              id?: number;
              status_type?: string;
              entity_type?: string;
              order?: number | null;
            }
          >) ?? {}
        );
        const confirmedStatus = pickInitialBookingStatus(statuses);
        // A resource that can't be read is still booked, by id alone and without its default
        // rate, as when it was missing from the v1 store; the failure is reported.
        const resources = await Promise.all(
          input.resources.map(resourceSpec =>
            resourceService.ensureItem(resourceSpec.id).catch(error => {
              errorService.reportError(
                'ProductionMutationService',
                ensureError(error)
              );
              return { id: resourceSpec.id };
            })
          )
        );

        const createdBookings: Booking[] = [];
        for (const [index, resourceSpec] of input.resources.entries()) {
          const resource = resources[index];
          const eventSeed = eventSeedForResourceAssignment(
            resourceSpec,
            productionDate
          );
          const booking = createBookingObject(bookingTypes, {
            resource,
            event: eventSeed,
            assignment: undefined,
            slot: { id: input.slotId },
            status: confirmedStatus ?? {}
          });
          const created = await bookingApi.createItem(
            formatBooking(booking) as BookingCreateDTO
          );
          createdBookings.push(created);
        }
        if (createdBookings.length > 0) {
          messageService.reportCache.emit({
            entities: createdBookings.map(payload => ({
              entity: 'booking',
              payload
            }))
          });
        }
        options?.onPersisted?.(createdBookings);
        await afterResourcingMutation(productionId, {
          bookingIds: createdBookings.flatMap(booking =>
            booking.id != null ? [booking.id] : []
          )
        });
        return createdBookings;
      }
    ),

    transferProductionsToScope: async request => {
      await interceptor.wrap(async () => {
        await api.transferProductionsToScope(request);
      })();

      await awaitReportSystemEffects(
        messageService,
        request.ids.flatMap(productionId =>
          getSystemEffectsForProductionResourcingUpdate({ productionId })
        )
      );
    }
  };
}

type ProductionDateWire = {
  start_date?: string;
  end_date?: string;
  all_day_duration_hours?: number | null;
  include_weekends?: boolean | null;
};

function eventSeedForResourceAssignment(
  resource: CreateResourceAssignmentResourceInput,
  productionDate: ProductionDateWire | null
): BookingEventFields | Record<string, never> {
  const schedule = resource.optimisticSchedules?.[0];
  if (schedule) {
    const fromSchedule =
      bookingBillingFromV1.workScheduleToEventFields(schedule);
    if (fromSchedule) {
      return fromSchedule;
    }
  }
  if (productionDate?.start_date && productionDate?.end_date) {
    return {
      date_type: 'RECURRING_DEFAULT',
      start_date: productionDate.start_date,
      end_date: productionDate.end_date,
      ...(productionDate.all_day_duration_hours != null
        ? { all_day_duration_hours: productionDate.all_day_duration_hours }
        : {}),
      ...(productionDate.include_weekends != null
        ? { include_weekends: productionDate.include_weekends }
        : {})
    };
  }
  return {};
}
